import React from "react";
import { Link } from "react-router-dom";
import "../../styles/components/match-hero.scss";

// Types
interface Team {
	teamId: string;
	teamName: string;
	teamLogo: string;
	season: number;
}

interface SeriesScore {
	team1: number;
	team2: number;
}

export interface MatchHeroProps {
	team1: Team;
	team2: Team;
	team1Score: number | null;
	team2Score: number | null;
	status: "upcoming" | "live" | "played";
	matchTime?: string;
	currentMap?: string;
	currentRound?: number;
	maxRounds?: number;
	seriesScore?: SeriesScore;
	liveMapNumber?: number;
	isViewingLiveMap?: boolean;
	hasIncompleteData?: boolean;
	demoDownloadButton?: React.ReactNode;
	getTeamLogoUrl: (logoUrl: string, teamId: string, season: number) => string;
	formatDateTime?: (dateString: string) => string;
}

const MatchHero: React.FC<MatchHeroProps> = ({
	team1,
	team2,
	team1Score,
	team2Score,
	status,
	matchTime,
	currentMap,
	currentRound,
	maxRounds,
	seriesScore,
	liveMapNumber,
	isViewingLiveMap,
	hasIncompleteData,
	demoDownloadButton,
	getTeamLogoUrl,
	formatDateTime,
}) => {
	const teamPlaceholderImage =
		"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23333'/%3E%3Ctext x='50' y='50' font-size='18' text-anchor='middle' alignment-baseline='middle' font-family='sans-serif' fill='%23fff'%3ENo Logo%3C/text%3E%3C/svg%3E";

	return (
		<div className="match-header">
			{/* Team 1 */}
			<div className="team-side team1-side">
				<Link to={`/teams/${team1.teamId}`} style={{ display: "block" }}>
					<img
						src={getTeamLogoUrl(team1.teamLogo, team1.teamId, team1.season)}
						alt={team1.teamName}
						className="team-logo"
						onError={(e) => {
							(e.target as HTMLImageElement).src = teamPlaceholderImage;
						}}
					/>
				</Link>
				<Link
					to={`/teams/${team1.teamId}`}
					style={{ textDecoration: "none", color: "inherit" }}
				>
					<h2 className="team-name">{team1.teamName}</h2>
				</Link>
			</div>

			{/* Team 1 Score */}
			<div className="team-score team1-score">
				{(status === "played" || status === "live") && (team1Score || 0)}
				{status === "upcoming" && "-"}
			</div>

			{/* Match Status */}
			<div className="match-status">
				{status === "upcoming" && matchTime && formatDateTime && (
					<div className="match-time">
						<span>{formatDateTime(matchTime)}</span>
					</div>
				)}
				{status === "live" && (
					<>
						{seriesScore && (
							<div
								className="series-score"
								style={{
									fontSize: "14px",
									color: "#888",
									marginBottom: "0.25rem",
								}}
							>
								({seriesScore.team1}-{seriesScore.team2})
							</div>
						)}
						<div className="live-indicator">
							<span className="live-badge">
								● LIVE
								{!isViewingLiveMap && liveMapNumber
									? ` (Map ${liveMapNumber})`
									: ""}
							</span>
						</div>
						{currentMap && (
							<div className="map-name">
								{currentMap.replace("de_", "")}
							</div>
						)}
						{isViewingLiveMap && currentRound && (
							<div className="round-info">
								Round {currentRound}/{maxRounds || 24}
							</div>
						)}
					</>
				)}
				{status === "played" && (
					<>
						{matchTime && formatDateTime && (
							<div className="match-date">
								<span>{formatDateTime(matchTime)}</span>
							</div>
						)}
						{currentMap && (
							<div className="map-name">
								<span className="map-name-full">{currentMap}</span>
								<span className="map-name-short">
									{currentMap.startsWith("Best of ")
										? `BO${currentMap.replace("Best of ", "")}`
										: currentMap}
								</span>
								{hasIncompleteData && (
									<span className="incomplete-data-indicator">
										*
										<span className="tooltip-text">
											Some match data may be incomplete or unavailable. Full
											statistics might not be shown.
										</span>
									</span>
								)}
							</div>
						)}
						{demoDownloadButton && (
							<div className="demo-download">{demoDownloadButton}</div>
						)}
					</>
				)}
			</div>

			{/* Team 2 Score */}
			<div className="team-score team2-score">
				{(status === "played" || status === "live") && (team2Score || 0)}
				{status === "upcoming" && "-"}
			</div>

			{/* Team 2 */}
			<div className="team-side team2-side">
				<Link
					to={`/teams/${team2.teamId}`}
					style={{ textDecoration: "none", color: "inherit" }}
				>
					<h2 className="team-name">{team2.teamName}</h2>
				</Link>
				<Link to={`/teams/${team2.teamId}`} style={{ display: "block" }}>
					<img
						src={getTeamLogoUrl(team2.teamLogo, team2.teamId, team2.season)}
						alt={team2.teamName}
						className="team-logo"
						onError={(e) => {
							(e.target as HTMLImageElement).src = teamPlaceholderImage;
						}}
					/>
				</Link>
			</div>
		</div>
	);
};

export default MatchHero;
